import Phaser from 'phaser';
import { COLORS, FONTS, fontStr } from '@/config';
import { gameState } from '@/state/store';
import {
  kravForOppgradering,
  naestTier,
  TIER_NAVN,
} from '@/systems/tier';
import type { TierKriterium } from '@/systems/tier';
import { tooltipManager } from '@/ui/TooltipManager';

const OKER = COLORS.ochre;
const OKER_CSS = '#d9a14b';

export class TierProgressWidget {
  private container?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private pulsTween?: Phaser.Tweens.Tween;

  constructor(
    private scene: Phaser.Scene,
    private x: number,
    private y: number,
    private width: number,
  ) {}

  mount(): void {
    this.container = this.scene.add.container(this.x, this.y);
    this.render();
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  destroy(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.pulsTween?.stop();
    this.pulsTween = undefined;
    this.container?.destroy(true);
    this.container = undefined;
  }

  private render(): void {
    if (!this.container) return;
    this.container.removeAll(true);
    this.pulsTween?.stop();
    this.pulsTween = undefined;

    const snap = gameState.snapshot;
    const neste = naestTier(snap.tier);
    if (!neste) {
      this.renderMaks();
      return;
    }

    const status = kravForOppgradering(snap.tier, gameState.beregnTierData());

    const radHoyde = 56;
    const hoyde = 110 + status.kriterier.length * (radHoyde + 6);

    const bg = this.scene.add
      .rectangle(0, 0, this.width, hoyde, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    this.container.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(0, 0, 4, hoyde, OKER, 1)
      .setOrigin(0, 0);
    this.container.add(venstreBorder);

    const tittel = this.scene.add
      .text(16, 12, `VEI MOT TIER ${neste}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: OKER_CSS,
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    this.container.add(tittel);

    const navn = this.scene.add
      .text(this.width - 16, 12, TIER_NAVN[neste], {
        fontFamily: FONTS.body,
        fontSize: fontStr(14),
        color: '#5a4a3a',
        fontStyle: 'italic',
      })
      .setOrigin(1, 0);
    this.container.add(navn);

    const erEller = status.kriterier.length >= 2;
    if (erEller) {
      const ellerTxt = this.scene.add
        .text(16, 48, 'ELLER ett av:', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(12),
          color: '#8b5e3c',
          fontStyle: '600',
        })
        .setOrigin(0, 0);
      this.container.add(ellerTxt);
    }

    let y = erEller ? 80 : 52;
    for (const kriterium of status.kriterier) {
      this.renderRad(0, y, kriterium);
      y += radHoyde + 6;
    }

    if (status.klart) {
      const klartBg = this.scene.add
        .rectangle(0, hoyde - 40, this.width, 40, COLORS.wine, 1)
        .setOrigin(0, 0);
      const klartTxt = this.scene.add
        .text(this.width / 2, hoyde - 20, 'Klar for oppgradering', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      this.container.add([klartBg, klartTxt]);
      this.pulsTween = this.scene.tweens.add({
        targets: klartBg,
        alpha: { from: 0.6, to: 1.0 },
        duration: 700,
        yoyo: true,
        repeat: -1,
      });
    }
  }

  private renderRad(x: number, y: number, kriterium: TierKriterium): void {
    if (!this.container) return;
    const label = this.scene.add
      .text(x + 16, y, kriterium.navn, {
        fontFamily: FONTS.body,
        fontSize: fontStr(13),
        color: '#1a1a1a',
        wordWrap: { width: this.width - 160 },
      })
      .setOrigin(0, 0);
    this.container.add(label);

    const naadd = kriterium.naadd;
    const krav = kriterium.krav;
    const verditxt = `${formaterTall(naadd)}/${formaterTall(krav)}`;
    const verdiTekst = this.scene.add
      .text(this.width - 16, y, verditxt, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(13),
        color: naadd >= krav ? '#3d5a4d' : '#5a4a3a',
        fontStyle: '700',
      })
      .setOrigin(1, 0);
    this.container.add(verdiTekst);

    const barY = y + 36;
    const barW = this.width - 32;
    const frac = Math.max(0, Math.min(1, krav > 0 ? naadd / krav : 0));
    const barBg = this.scene.add
      .rectangle(x + 16, barY, barW, 4, COLORS.beigeDark, 1)
      .setOrigin(0, 0);
    const barFyll = this.scene.add
      .rectangle(
        x + 16,
        barY,
        Math.max(2, Math.round(barW * frac)),
        4,
        naadd >= krav ? COLORS.forest : OKER,
        1,
      )
      .setOrigin(0, 0);
    this.container.add([barBg, barFyll]);

    const trefftekst = tierKriteriumTooltip(kriterium);
    if (trefftekst) {
      const hitArea = this.scene.add
        .rectangle(x + 16, y, barW, barY - y + 6, 0xffffff, 0.001)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: false });
      this.container.add(hitArea);
      const containerX = this.x;
      const containerY = this.y;
      tooltipManager.attach(
        hitArea,
        () => trefftekst,
        () => ({ x: containerX + x + 16 + barW / 2, y: containerY + y }),
      );
    }
  }

  private renderMaks(): void {
    if (!this.container) return;
    const hoyde = 80;
    const bg = this.scene.add
      .rectangle(0, 0, this.width, hoyde, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    this.container.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(0, 0, 4, hoyde, 0xb8860b, 1)
      .setOrigin(0, 0);
    this.container.add(venstreBorder);

    const tittel = this.scene.add
      .text(this.width / 2, hoyde / 2, 'Tier S — Maks oppnådd', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#b8860b',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.container.add(tittel);
  }
}

function formaterTall(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1_000)}k`;
  if (Number.isInteger(n)) return n.toLocaleString('nb-NO');
  return n.toFixed(1);
}

function tierKriteriumTooltip(kriterium: TierKriterium): string {
  const n = kriterium.navn;
  if (n === 'Utgivelser') return 'Antall bøker du har lansert. Hver utgitt bok teller når den passerer trykk-fasen.';
  if (n === 'Total inntekt') return 'Sum royalty fra alle dine bøker siden start. Forskudd, lydbøker og film teller med.';
  if (n === 'Brage-nominasjon') return 'En bok må ha snitt-anmeldelse 5.0 eller mer for å være Brage-kandidat. Nominasjon kunngjøres i oktober.';
  if (n === 'Beste enkeltbok-salg') return 'Bokens totale salg (papir + lydbok). 1 000 eks = bestselger, 10 000 = nasjonal-storfilm.';
  if (n === 'Brage-pris') return 'Brage-vinner i en kategori. Krever snitt-anmeldelse 6.0 og må nomineres først.';
  if (n === 'Litterære priser totalt') return 'Antall priser dine bøker har vunnet (Brage, Cappelen-prisen, Bokhandlerprisen, m.fl.).';
  if (n === 'Bøker med snitt ≥ 5.0') return 'Antall bøker hvor minst 4 kritikere har gitt snitt-terninger 5.0 eller mer.';
  if (n.startsWith('Bestselgere')) return `Bøker som har solgt minst ${formaterTall(kriterium.krav)} eksemplarer. Krever lang tid på markedet eller stor kampanje.`;
  return `${n}: ${formaterTall(kriterium.naadd)} av ${formaterTall(kriterium.krav)} oppnådd.`;
}
